import * as localforage from 'localforage';
import ngRedux from 'ng-redux';

import { CreateStorageMiddleware, GetInitialStorageState, hasBrowserSupport, RootState, StorageEncoding, WorldItem } from '../models';

type MemoryStorageDriver = {
  _driver: string;
} & LocalForageDriver;

type PossibleStoredState = string | RootState | Blob | undefined;

const StorageEncodings: {
  BINARY: StorageEncoding;
  BLOB: StorageEncoding;
  NONE: StorageEncoding;
} = {
  BINARY: 'binary',
  BLOB: 'blob',
  NONE: 'none',
};

function isCompatible(worldReference: WorldItem, world: WorldItem): boolean {
  return world.countryCode === worldReference.countryCode &&
         world.customerType === worldReference.customerType &&
         world.sessionToken === worldReference.sessionToken;
}

/**
 * Convert a Unicode string to a string in which
 * each 16-bit unit occupies only one byte
 * @param target The string to convert
 */
function toBinary(target: string): string {
  const codeUnits = new Uint16Array(target.length);
  for (let i = 0; i < codeUnits.length; i++) {
    codeUnits[i] = target.charCodeAt(i);
  }
  return [...new Uint8Array(codeUnits.buffer)].map((value) => String.fromCharCode(value)).join('');
}

/**
 * Convert a string in which
 * each 16-bit unit occupies only one byte
 * to a Unicode string
 * @param target The string to convert
 */
function fromBinary(target: string): string {
  const bytes = new Uint8Array(target.length);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = target.charCodeAt(i);
  }
  return [...new Uint16Array(bytes.buffer)].map((value) => String.fromCharCode(value)).join('');
}

// tslint:disable-next-line:no-var-requires
const memoryStorageDriver: MemoryStorageDriver = require('localforage-memoryStorageDriver');
const binaryStringEncodingSupported: boolean = hasBrowserSupport('atob') &&
                                               hasBrowserSupport('btoa') &&
                                               hasBrowserSupport('Uint16Array') &&
                                               hasBrowserSupport('Uint8Array');
const blobSupported: boolean = hasBrowserSupport('Blob') && hasBrowserSupport('FileReader');

const encodeState = (encodingType: StorageEncoding, state: RootState): PossibleStoredState => {
  let encodedState: PossibleStoredState = state;
  if (encodingType === StorageEncodings.BINARY && binaryStringEncodingSupported) {
    try {
      encodedState = btoa(toBinary(JSON.stringify(state)));
    } catch (e) {
      encodedState = state;
    }
  }
  if (encodingType === StorageEncodings.BLOB && blobSupported) {
    try {
      encodedState = new Blob([JSON.stringify(state)], { type: 'application/json' });
    } catch (e) {
      encodedState = state;
    }
  }
  return encodedState;
};

const decodeState = ($q: ng.IQService, state: PossibleStoredState): ng.IPromise<PossibleStoredState> => {
  const deferred: ng.IDeferred<PossibleStoredState> = $q.defer();
  let decodedState: PossibleStoredState = state;
  if (state) {
    if (typeof state === 'object') {
      if (blobSupported && state instanceof window.Blob) {
        const reader: FileReader = new FileReader();
        reader.addEventListener('loadend', () => {
          try {
            decodedState = JSON.parse(reader.result as string);
          } catch (e) {
            decodedState = state;
          }
          deferred.resolve(decodedState);
        });
        reader.readAsText(state);
      } else {
        decodedState = state;
        deferred.resolve(decodedState);
      }
    } else if (typeof state === 'string' && binaryStringEncodingSupported) {
      try {
        decodedState = JSON.parse(fromBinary(atob(state))) as RootState;
      } catch (e) {
        decodedState = state;
      }
      deferred.resolve(decodedState);
    } else {
      deferred.resolve(decodedState);
    }
  } else {
    deferred.resolve(decodedState);
  }
  return deferred.promise;
};

export const storageMiddleware: CreateStorageMiddleware = (settings: { key: string; type: string; }): ngRedux.Middleware => {
  const { key, type } = settings;
  localforage.defineDriver(memoryStorageDriver);
  localforage.config({
    driver: [type, memoryStorageDriver._driver],
    name: key,
    version: 1.0,
    storeName: key,
    description : 'Custom Standard Oligo store',
  });
  let driverType: string;
  localforage.ready(() => {
    driverType = localforage.driver();
  });
  return (store: ngRedux.MiddlewareArg<ngRedux.Dispatch, RootState>) => (next) => (action) => {
    const result = next(action);
    const state: RootState = store.getState();
    let storageEncoding: StorageEncoding = StorageEncodings.NONE;
    if (state && state.world && state.world.clientConfig) {
      storageEncoding = state.world.clientConfig.storageEncoding || StorageEncodings.NONE;
    }
    localforage.setItem(key, driverType === type ? encodeState(storageEncoding, state) : state);
    return result;
  };
};

export const storageState: GetInitialStorageState<ng.IPromise<RootState>> = ($q: ng.IQService, settings: {
  key: string;
  type: string;
  defaultResponse: RootState,
}): ng.IPromise<RootState> => {
  const deferred: ng.IDeferred<RootState> = $q.defer();
  const { key, defaultResponse: defaultState } = settings;
  const { world: worldReference } = defaultState;
  const { sessionStorage } = window;
  const sessionFlagKey: string = `${key}-session`;
  const hasActiveSession: boolean = sessionStorage && sessionStorage.getItem(sessionFlagKey) === 'true';
  if (!hasActiveSession) {
    localforage.clear().then(() => {
      sessionStorage.setItem(sessionFlagKey, 'true');
      deferred.resolve(defaultState);
    });
  } else {
    localforage.getItem<PossibleStoredState>(key).then((data) => {
      decodeState($q, data).then((storedState: PossibleStoredState) => {
        const state = storedState as RootState;
        if (state && typeof state === 'object') {
          if (!isCompatible(worldReference, state.world)) {
            localforage.removeItem(key).then(() => {
              deferred.resolve(defaultState);
            });
          } else {
            deferred.resolve(state);
          }
        } else {
          deferred.resolve(defaultState);
        }
      }, () => {
        deferred.resolve(defaultState);
      });
    });
  }
  return deferred.promise;
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/service/storage-service.ts